import { INPUT_RING_CAPACITY } from './constants.js'
import type { PlayerInput } from './types.js'

/** Fixed-capacity ring of inputs indexed by sequence number.
 *
 *  Used on the client to hold unacked inputs for replay, and on the server to
 *  buffer/dedupe arriving inputs per client. Sequence numbers are monotonic;
 *  inserting an old (already-evicted) or duplicate seq is a no-op. */
export class InputRing<T extends PlayerInput = PlayerInput> {
  private readonly slots: (T | undefined)[]
  private _newest = -1
  private _oldest = 0
  private _count = 0

  constructor(readonly capacity: number = INPUT_RING_CAPACITY) {
    if (capacity <= 0 || (capacity & (capacity - 1)) !== 0) {
      throw new Error('InputRing capacity must be a power of two')
    }
    this.slots = new Array(capacity).fill(undefined)
  }

  get size(): number { return this._count }
  get newestSeq(): number { return this._newest }
  get oldestSeq(): number { return this._count === 0 ? -1 : this._oldest }

  /** Returns true if the input was accepted (new, in-window). */
  push(input: T): boolean {
    const seq = input.seq
    if (!Number.isInteger(seq) || seq < 0) return false
    if (this._count > 0 && seq <= this._newest - this.capacity) return false // too old
    if (this.get(seq)) return false // duplicate
    this.slots[seq & (this.capacity - 1)] = input
    if (seq > this._newest) this._newest = seq
    // Out-of-order arrival below the current oldest (reordered packets) must
    // still be enumerable by since()/shift(); only the capacity floor evicts.
    if (this._count === 0 || seq < this._oldest) this._oldest = seq
    // Evict anything older than newest - capacity + 1.
    const floor = this._newest - this.capacity + 1
    if (this._oldest < floor) this._oldest = floor
    this._count = this.countRange()
    return true
  }

  get(seq: number): T | undefined {
    if (seq < 0 || seq > this._newest || seq <= this._newest - this.capacity) return undefined
    const s = this.slots[seq & (this.capacity - 1)]
    return s && s.seq === seq ? s : undefined
  }

  /** Drop everything with seq <= ackSeq (the authority has consumed them). */
  ackUpTo(ackSeq: number): void {
    for (let s = this._oldest; s <= Math.min(ackSeq, this._newest); s++) {
      const i = s & (this.capacity - 1)
      if (this.slots[i]?.seq === s) this.slots[i] = undefined
    }
    if (ackSeq >= this._oldest) this._oldest = ackSeq + 1
    this._count = this.countRange()
  }

  /** All retained inputs with seq > afterSeq, ascending. */
  since(afterSeq: number): T[] {
    const out: T[] = []
    for (let s = Math.max(this._oldest, afterSeq + 1); s <= this._newest; s++) {
      const v = this.get(s)
      if (v) out.push(v)
    }
    return out
  }

  /** Pop the oldest retained input (server-side consumption). */
  shift(): T | undefined {
    for (let s = this._oldest; s <= this._newest; s++) {
      const v = this.get(s)
      if (v) {
        this.slots[s & (this.capacity - 1)] = undefined
        this._oldest = s + 1
        this._count = this.countRange()
        return v
      }
    }
    return undefined
  }

  /** Last N inputs, ascending (for redundant sends). */
  tail(n: number): T[] {
    return this.since(this._newest - n)
  }

  clear(): void {
    this.slots.fill(undefined)
    this._newest = -1
    this._oldest = 0
    this._count = 0
  }

  private countRange(): number {
    let c = 0
    for (let s = this._oldest; s <= this._newest; s++) if (this.get(s)) c++
    return c
  }
}

export const idleInput = (seq = 0, t = 0, tick = 0): PlayerInput => ({
  seq, tick, mx: 0, my: 0, aim: 0, throw: false, shield: false, dash: false, t,
})
